import type { ResourceMapNodeHealth } from "@k8s-dashboard/contracts";

/**
 * Resource Map 시각 미리보기의 순수 레이아웃 (ADR 0020 · #8 M4-I13, 결정 7)
 * --------------------------------------------------------------------------
 * 타입·상수·결정적 배치만 있습니다 — React·DOM·난수·타이머·의존성이 없고
 * O(N)입니다. 그리기는 ResourceMapGraph.tsx가 맡습니다.
 */

/** 시각 미리보기에 그리는 노드 상한(문서화된 하드 캡). */
export const RESOURCE_MAP_VISIBLE_NODE_CAP = 64;
/** 시각 미리보기에 그리는 간선 상한(문서화된 하드 캡). */
export const RESOURCE_MAP_VISIBLE_EDGE_CAP = 192;

/** SVG viewBox 한 변(단위 없는 좌표계). */
export const RESOURCE_MAP_VIEWBOX = 640;
/**
 * 노드 카드(HTML 오버레이 슬롯)의 최대 너비 — CSS의 **최악 값**(모바일
 * `max-width: 40%`)입니다. 데스크톱 28%는 이보다 작으므로 같은 여백에 들어갑니다.
 */
export const RESOURCE_MAP_NODE_SLOT_MAX_FRACTION = 0.4;
/** focus 윤곽선 + 오프셋의 보수 상계(viewBox 단위). */
export const RESOURCE_MAP_NODE_OUTLINE_UNITS = 6;
/**
 * 배치 좌표의 안전 여백 — 모든 노드 **중심**은 [INSET, VIEWBOX−INSET] 안이고,
 * 여백은 카드 반너비(0.20×640=128) + 윤곽선 상계(6)를 덮으므로 **카드 전체와
 * 윤곽선**이 캔버스 안에 남습니다(테스트가 이 부등식을 고정합니다).
 */
export const RESOURCE_MAP_SAFE_INSET = 136;
/** 간선 라벨을 화살촉(간선 중간)에서 수직으로 띄우는 거리(viewBox 단위). */
export const RESOURCE_MAP_EDGE_LABEL_OFFSET = 16;

const CENTER = RESOURCE_MAP_VIEWBOX / 2;
/** 링 r(1부터)의 자리 수 = RING_BASE × r — 바깥 링일수록 둘레가 길어 더 담습니다. */
const RING_BASE = 8;

/** 관계 종류별 대시 패턴 — 색과 무관하게 구분됩니다. */
export const RESOURCE_MAP_EDGE_PATTERNS: Readonly<Record<string, string>> = {
  owner: "0",
  selects: "6 3",
  routesTo: "2 3",
  mounts: "8 3 2 3",
  scales: "1 4",
  protects: "10 4",
  storage: "4 2 1 2",
  serviceAccount: "3 6",
};

export interface ResourceMapGraphNode {
  /** 목록과 같은 안정 키(UID 우선). */
  key: string;
  kind: string;
  namespace: string;
  name: string;
  uid: string;
  resolved: boolean;
  /** 서버가 준 캐시 전용 건강(닫힌 집합). 클라이언트에서 계산하지 않습니다. */
  health: ResourceMapNodeHealth;
  /** 서버가 focus로 표시한 노드. */
  focus: boolean;
  /** 선택 시 URL로 옮길 GVR 경로(`group/version/resource`). 없으면 탐색 불가. */
  resource: string;
}

export interface ResourceMapGraphEdge {
  from: string;
  to: string;
  /** 관계 종류(owner·selects·routesTo·mounts·scales·protects·storage·serviceAccount). */
  type: string;
  resolved: boolean;
}

export interface PlacedNode {
  node: ResourceMapGraphNode;
  x: number;
  y: number;
}

export interface Point {
  x: number;
  y: number;
}

/**
 * 결정적 배치. 보이는 부분집합은 **실제 focus를 언제나 포함**하고(서버 순서상
 * 상한 뒤에 있어도), 나머지는 서버 상대 순서 그대로 앞에서부터 상한까지입니다.
 * focus(없으면 서버 첫 노드)는 중심, 나머지는 동심원 링에 균등 각도로 놓이며
 * 링 반지름은 필요한 링 수로 나눠 **모든 좌표가 안전 여백 안**에 들어옵니다 —
 * 입력이 같으면 좌표가 언제나 같고, O(N)입니다.
 */
export function placeResourceMapNodes(nodes: ReadonlyArray<ResourceMapGraphNode>): PlacedNode[] {
  const focusIdx = nodes.findIndex((n) => n.focus);
  const others: ResourceMapGraphNode[] = [];
  const otherCap = focusIdx >= 0 ? RESOURCE_MAP_VISIBLE_NODE_CAP - 1 : RESOURCE_MAP_VISIBLE_NODE_CAP;
  for (let i = 0; i < nodes.length && others.length < otherCap; i += 1) {
    if (i !== focusIdx) others.push(nodes[i]);
  }
  const out: PlacedNode[] = [];
  let center: ResourceMapGraphNode | undefined;
  if (focusIdx >= 0) center = nodes[focusIdx];
  else if (others.length > 0) center = others.shift();
  if (!center) return out;
  out.push({ node: center, x: CENTER, y: CENTER });

  let rings = 0;
  let capacity = 0;
  while (capacity < others.length) {
    rings += 1;
    capacity += RING_BASE * rings;
  }
  const step = rings > 0 ? (CENTER - RESOURCE_MAP_SAFE_INSET) / rings : 0;
  let ring = 1;
  let slot = 0;
  for (const node of others) {
    const perRing = RING_BASE * ring;
    const angle = (2 * Math.PI * slot) / perRing - Math.PI / 2;
    const radius = ring * step;
    out.push({
      node,
      x: Math.round(CENTER + radius * Math.cos(angle)),
      y: Math.round(CENTER + radius * Math.sin(angle)),
    });
    slot += 1;
    if (slot >= perRing) {
      ring += 1;
      slot = 0;
    }
  }
  return out;
}

/**
 * 간선 기하: 화살촉은 from→to의 **중간점**(polyline 중간 꼭짓점), 라벨은 그
 * 중간점에서 간선에 수직으로 RESOURCE_MAP_EDGE_LABEL_OFFSET만큼 띄운 점입니다
 * — 두 좌표는 언제나 다릅니다(길이 0 간선은 위쪽으로 띄웁니다).
 */
export function edgeGeometry(a: Point, b: Point): { arrow: Point; label: Point } {
  const arrow = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  const nx = len === 0 ? 0 : -dy / len;
  const ny = len === 0 ? -1 : dx / len;
  return {
    arrow,
    label: {
      x: Math.round(arrow.x + nx * RESOURCE_MAP_EDGE_LABEL_OFFSET),
      y: Math.round(arrow.y + ny * RESOURCE_MAP_EDGE_LABEL_OFFSET),
    },
  };
}
